import { areIntervalsOverlapping, parseISO } from 'date-fns'
import { Draft } from 'immer'
import { ISO8601Duration } from '@opallabs/concepts'
import { PlanResponse } from '../types'

type BlockDef = PlanResponse['data']['blocks']['by_id'][string]

export const resolveOverlappingBlocks = (
  modifiedBlockId: string,
  modifiedCategoryId: string
) => {
  return (draftResponse: Draft<PlanResponse>) => {
    const blocksForCategory =
      draftResponse.data.blocks.by_category_with_position[modifiedCategoryId]
    const updatedBlock = draftResponse.data.blocks.by_id[modifiedBlockId]

    if (!blocksForCategory || !updatedBlock) return draftResponse

    for (const blockPos of blocksForCategory) {
      const blockDef = draftResponse.data.blocks.by_id[blockPos.id]

      if (
        blockPos.id !== updatedBlock.id &&
        blockPos.row_index !== null &&
        blockDef.row_index !== null &&
        isOverlappingBlock(updatedBlock, blockDef)
      ) {
        blockPos.row_index++
        blockDef.row_index = blockPos.row_index
        draftResponse = resolveOverlappingBlocks(
          blockDef.id,
          modifiedCategoryId
        )(draftResponse)
      }
    }

    return draftResponse
  }
}

/**
 * Checks to see if two blocks are both
 * 1) in the same row_index
 * 2) have overlapping dates
 */
const isOverlappingBlock = (
  updatedBlock: Draft<BlockDef>,
  checkedBlock: Draft<BlockDef>
) => {
  /**
   * Both blocks must be positioned at the same row_index
   * and have a start_at to potentially overlap
   */
  if (
    updatedBlock.row_index !== checkedBlock.row_index ||
    updatedBlock.row_index === null ||
    checkedBlock.row_index === null ||
    updatedBlock.start_at === null ||
    checkedBlock.start_at === null
  ) {
    return false
  }

  const updatedStartDate = parseISO(updatedBlock.start_at)
  const updatedBlockDates = {
    startDate: updatedStartDate,
    endDate: updatedBlock.duration
      ? ISO8601Duration.add(updatedStartDate, updatedBlock.duration)
      : null,
  }

  const checkedStartDate = parseISO(checkedBlock.start_at)
  const checkedBlockDates = {
    startDate: checkedStartDate,
    endDate: checkedBlock.duration
      ? ISO8601Duration.add(checkedStartDate, checkedBlock.duration)
      : null,
  }

  return areIntervalsOverlapping(
    {
      start: updatedBlockDates.startDate,
      end: updatedBlockDates.endDate || updatedBlockDates.startDate,
    },
    {
      start: checkedBlockDates.startDate,
      end: checkedBlockDates.endDate || checkedBlockDates.startDate,
    },
    { inclusive: true }
  )
}
